import { useState } from 'react';
import { Box } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import useCreateCountry from './queries/useCreateCountry';
import useGetCountry from './queries/useGetCountry';
import useUpdateCountry from './queries/useUpdateCountry';

import FormBase from '@/components/forms/new/FormBase';
import { LoadingIndicator } from 'components/v4';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import {
    breadCrumbTrail,
    breadCrumbTrailEdit,
    addHeader,
    editHeader,
    initialState,
    countriesPath,
    codesInfoText,
} from './constants';
import { countrySchema } from './schema';
import CountryNameField from './formFields/CountryNameField';
import CountryCodeField from './formFields/CountryCodeField';
import CountryAlpha2Field from './formFields/CountryAlpha2Field';
import CountryAlpha3Field from './formFields/CountryAlpha3Field';
import CountryCodesField from './formFields/CountryCodesField';

const CountryForm = ({ mode = 'add' }) => {
    const isEdit = mode === 'edit';
    const navigate = useNavigate();
    const { id } = useParams();
    const { darkMode } = useSelector((state) => state.settings);
    const [error, setError] = useState(null);

    const {
        data: countryData,
        isLoading: countryLoading,
        isFetching: countryFetching,
        isError: notFound,
    } = useGetCountry(id, isEdit);

    const handleSuccess = () => {
        toast.success(
            isEdit
                ? 'Country updated successfully!'
                : 'Country created successfully!',
        );
        navigate(countriesPath);
    };

    const handleError = (err) => {
        setError(
            err?.response?.data?.message ||
                `Failed to ${isEdit ? 'update' : 'create'} country. Please try again.`,
        );
    };

    const { mutate: createCountry, isPending: createPending } =
        useCreateCountry(handleSuccess, handleError);

    const { mutate: updateCountry, isPending: updatePending } =
        useUpdateCountry(handleSuccess, handleError);

    const isPending = isEdit ? updatePending : createPending;

    const finalFormSubmit = async (values, { setErrors, setTouched }) => {
        setError(null);

        let validated;
        try {
            validated = await countrySchema.validate(values, {
                abortEarly: false,
            });
        } catch (validationError) {
            const fieldErrors = {};
            (validationError.inner || []).forEach((innerError) => {
                if (innerError.path && !fieldErrors[innerError.path]) {
                    fieldErrors[innerError.path] = innerError.message;
                }
            });
            if (Object.keys(fieldErrors).length > 0) {
                setErrors(fieldErrors);
                setTouched(
                    Object.keys(fieldErrors).reduce(
                        (touchedFields, field) => ({
                            ...touchedFields,
                            [field]: true,
                        }),
                        {},
                    ),
                );
            } else {
                setError(validationError.message);
            }
            return;
        }

        const payload = {
            ...validated,
            nationalCodes: values.nationalCodes,
            multiNationalCodes: values.multiNationalCodes,
        };

        if (isEdit) {
            updateCountry({ id: values.id, ...payload });
        } else {
            createCountry(payload);
        }
    };

    const formFields = (errors, touched, values, setFieldValue) =>
        [
            <CountryNameField
                key="name"
                errors={errors}
                touched={touched}
                darkMode={darkMode}
            />,
            <CountryCodeField
                key="countryCode"
                errors={errors}
                touched={touched}
                darkMode={darkMode}
            />,
            <CountryAlpha2Field
                key="alpha2"
                errors={errors}
                touched={touched}
                darkMode={darkMode}
            />,
            <CountryAlpha3Field
                key="alpha3"
                errors={errors}
                touched={touched}
                darkMode={darkMode}
            />,
            <Alert
                key="codes-info"
                status="info"
                sx={{ margin: '10px 0' }}
                data-codes-section-divider>
                <AlertIcon />
                <AlertDescription>{codesInfoText}</AlertDescription>
            </Alert>,
            <CountryCodesField
                key="nationalCodes"
                name="nationalCodes"
                label="National Codes"
                placeholder="Please enter National Codes"
                exampleText="eg. 325, 330, 518, 957"
                values={values}
                setFieldValue={setFieldValue}
                isDisabled={isPending}
            />,
            <CountryCodesField
                key="multiNationalCodes"
                name="multiNationalCodes"
                label="Multi-National Codes"
                placeholder="Please enter Multi-National Codes"
                exampleText="eg. 800, 822"
                values={values}
                setFieldValue={setFieldValue}
                isDisabled={isPending}
                stackPaddingTop="10px"
            />,
            error && (
                <Alert key="error" status="error" sx={{ margin: '25px 0' }}>
                    <AlertIcon />
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            ),
        ].filter(Boolean);

    if (isEdit && (countryLoading || countryFetching)) {
        return <LoadingIndicator />;
    }

    if (isEdit && notFound) {
        return (
            <FormBase
                breadCrumbTrail={breadCrumbTrailEdit}
                header={editHeader}
                initialValues={initialState}
                finalFormSubmit={async () => {}}
                hideSubmitButton
                backLink={[countriesPath, { state: { index: 0 } }]}>
                {() => [
                    <p key="not-found">
                        This country no longer exists. It may have already been
                        deleted.
                    </p>,
                ]}
            </FormBase>
        );
    }

    const formInitialValues = isEdit
        ? {
              id: countryData?.id,
              name: countryData?.name || '',
              countryCode: countryData?.countryCode || '',
              alpha2: countryData?.alpha2 || '',
              alpha3: countryData?.alpha3 || '',
              nationalCodes: countryData?.nationalCodes || [],
              multiNationalCodes: countryData?.multiNationalCodes || [],
          }
        : initialState;

    return (
        <Box
            sx={{
                '[data-codes-section-divider] + hr': {
                    borderColor: 'transparent',
                },
            }}>
            <FormBase
                isSubmitDisabled={isPending}
                isLoading={isPending}
                breadCrumbTrail={isEdit ? breadCrumbTrailEdit : breadCrumbTrail}
                header={isEdit ? editHeader : addHeader}
                initialValues={formInitialValues}
                finalFormSubmit={finalFormSubmit}
                backLink={[countriesPath, { state: { index: 0 } }]}>
                {({ errors, touched, values, setFieldValue }) =>
                    formFields(errors, touched, values, setFieldValue)
                }
            </FormBase>
        </Box>
    );
};

export default CountryForm;
